import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Clock, Check, FileText, Download, ChevronRight, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

export function CourseVideoView({ 
  selectedCourse, lessons, activeLesson, 
  setActiveLesson, videoRef, 
  handleVideoTimeUpdate, handleMarkComplete 
}) {
  const completedCount = lessons.filter(l => l.completed).length;
  const progressPercent = Math.round((completedCount / lessons.length) * 100);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="course-detail-view">
      {/* Sticky Header */}
      <div className="bg-[#141417] border-b border-[#27272A] px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/courses" className="text-[#71717A] hover:text-white">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {selectedCourse.title}
              </h1>
              <p className="text-[#71717A] text-sm">{completedCount}/{lessons.length} lessons completed</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32">
              <Progress value={progressPercent} className="h-2" />
            </div>
            <span className="text-[#007AFF] font-mono">{progressPercent}%</span>
            <Link to="/">
              <Button variant="outline" className="border-[#27272A] text-[#A1A1AA]" data-testid="back-home-header">
                <Home className="w-4 h-4 mr-2" /> Home
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Video Player and Notes */}
        <div className="flex-1 p-6">
          <div className="bg-[#141417] border border-[#27272A] rounded-md overflow-hidden mb-6">
            <div className="aspect-video bg-black">
              {activeLesson.video_url.includes('youtube.com') ? (
                <iframe
                  src={activeLesson.video_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={activeLesson.title}
                />
              ) : (
                <video
                  ref={videoRef}
                  src={activeLesson.video_url}
                  className="w-full h-full"
                  controls
                  onTimeUpdate={handleVideoTimeUpdate}
                />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {activeLesson.title}
                  </h2>
                  <p className="text-[#71717A] text-sm flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4" /> {activeLesson.duration}
                  </p>
                </div>
                {activeLesson.completed ? (
                  <div className="flex items-center gap-2 text-[#00FF66]">
                    <Check className="w-5 h-5" />
                    <span>Completed</span>
                  </div>
                ) : (
                  <Button 
                    className="btn-primary" 
                    onClick={handleMarkComplete}
                    data-testid="mark-complete-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Mark Complete
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Lesson Notes */}
          <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#007AFF]" /> Lesson Notes
              </h3>
              <Button variant="ghost" className="text-[#007AFF]" data-testid="download-notes-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
            <div className="prose prose-invert max-w-none">
              <p className="text-[#A1A1AA]">
                This lesson covers key concepts in {activeLesson.title.toLowerCase()}. 
                Take notes as you watch and practice the strategies demonstrated.
              </p>
              <ul className="text-[#A1A1AA] space-y-2 mt-4">
                <li>Key concept 1: Understanding market fundamentals</li>
                <li>Key concept 2: Technical analysis indicators</li>
                <li>Key concept 3: Risk management strategies</li>
                <li>Key concept 4: Entry and exit points</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Lesson List Sidebar */}
        <div className="w-80 border-l border-[#27272A] bg-[#141417] p-4 overflow-y-auto max-h-[calc(100vh-80px)]">
          <h3 className="text-white font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Course Lessons
          </h3>
          <div className="space-y-2">
            {lessons.map((lesson, index) => (
              <button
                key={lesson.id}
                onClick={() => setActiveLesson(lesson)}
                className={`w-full text-left p-3 rounded-md transition-colors ${
                  activeLesson?.id === lesson.id 
                    ? 'bg-[#007AFF]/10 border border-[#007AFF]/50' 
                    : 'bg-[#09090B] border border-[#27272A] hover:border-[#007AFF]/30'
                }`}
                data-testid={`lesson-btn-${lesson.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-mono ${
                    lesson.completed 
                      ? 'bg-[#00FF66]/10 text-[#00FF66]' 
                      : 'bg-[#27272A] text-[#A1A1AA]'
                  }`}>
                    {lesson.completed ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${activeLesson?.id === lesson.id ? 'text-white' : 'text-[#A1A1AA]'}`}>
                      {lesson.title}
                    </p>
                    <p className="text-[#71717A] text-xs">{lesson.duration}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
